<template>
  <div class="points-calendar">
    <FullCalendar :period="calendarPeriod" :attributes="calendarAttributes">
      <template #day-content="{ day, attributes }">
        <!-- LOADER -->
        <div v-if="loading" class="custom-calendar--day">
          <div class="custom-calendar--day-content-wrapper opened">
            <div class="date-content">
              <div class="mb-1 w-100">
                <Skeleton
                  class="date-content-skeleton--circle ml-1"
                  width="27px"
                  height="27px"
                />
              </div>

              <div class="w-100 text-right ">
                <Skeleton
                  class="date-content-skeleton--text"
                  width="40%"
                  height="12px"
                />
              </div>

              <div class="w-100 text-right">
                <Skeleton
                  class="date-content-skeleton--text"
                  width="50%"
                  height="12px"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- DADOS -->
        <div
          v-else
          :class="{
            'custom-calendar--day__disabled':
              !attributes || attributes.length === 0
          }"
          class="custom-calendar--day"
        >
          <div
            v-for="(attribute, i) in attributes"
            :key="`day-attr-${i}`"
            class="custom-calendar--day-content-wrapper opened"
            @mouseenter="
              attribute.customData.showDetail = true
              bindMouseMove($event, attribute)
            "
            @mouseleave="
              attribute.customData.showDetail = false
              unbindMouseMove($event)
            "
            @click="mouseClick(day.date, attribute)"
          >
            <div class="date-header">
              <div>
                <span class="disponibility-flag">
                  <span class="day-label">{{ day.day }}</span>
                </span>
              </div>

              <!-- DIFERENCIAL + PREÇO / CALENDARIO COMPRIMIDO -->
              <div class="price__normal">
                <!-- QUANTIDADE DE HÓSPEDES -->
                <Paragraph fontSize="small" class="compre-guest">
                  {{ attribute.customData.guest }}
                </Paragraph>

                <!-- PREÇO -->
                <Paragraph fontSize="small" fontWeight="bold" class="price">
                  {{ attribute.customData.price }} pts
                </Paragraph>
              </div>
            </div>
          </div>
        </div>
      </template>
    </FullCalendar>

    <!-- TOOLTIP DO HOVER -->
    <PointsDayDetailsTooltip
      :value="
        dayCurrentTargetContent
          ? dayCurrentTargetContent.customData.showDetail
          : false
      "
      :attributes="dayCurrentTargetContent"
      :top="currentDescTooltipPosition.y"
      :left="currentDescTooltipPosition.x"
      class="custom-details-tooltip"
    ></PointsDayDetailsTooltip>
  </div>
</template>

<script>
/**===============================================
 * Componente do calendário de tarifas e allotment
 * =============================================== */
import FullCalendar from '@/components/hotel/rates/FullCalendar'
import PointsDayDetailsTooltip from '@/components/hotel/points/DayDetailsTooltip'
import Skeleton from '@/components/_base/elements/Skeleton'
import Paragraph from '@/components/_base/elements/Paragraph'

export default {
  name: 'CalendarPoints',
  components: {
    FullCalendar,
    PointsDayDetailsTooltip,
    Skeleton,
    Paragraph
  },
  props: {
    /**
     * Período mostrado no calendário
     *
     * @example { rawStart: Datetime, start: String, rawEnd: Datetime, end: String }
     */
    calendarPeriod: {
      type: Object
    },
    /** Atributos que populam o calendário */
    calendarAttributes: {
      type: Array,
      default: () => []
    },

    /** Mostra skeleton loader */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * Tipo de disponibilidade.
     * Valores possíveis: 'letsbook' | 'fechado-in-out' | 'vendido'
     */
    disponibility: {
      type: String,
      validator: function(value) {
        /** O valor deve ser um dos possíveis */
        return ['letsbook', 'fechado-in-out', 'vendido'].indexOf(value) > -1
      }
    },
    /** Apresenta o mínimo de noites */
    minNights: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      /** Posição da tooltip visível de detalhes */
      currentDescTooltipPosition: {
        x: 0,
        y: 0
      },
      /** Coordenadas do mouse */
      mouseCoords: {
        x: 0,
        y: 0
      },
      dayCurrentTarget: null /** Dia que está com o cursor do mouse */,
      dayCurrentTargetContent: null /** Conteúdo do dia que está com o cursor do mouse */,
      dayCurrentTooltipTarget: null /** Tooltip visível ??? Talvez possa ser removido */,
      currentAnimationFrame: null /** Animação atual */
    }
  },

  methods: {
    /** Verifica se ao clicar deve ser aberto ou não o modal de edição
     * a edição só ocorrera no caso de click em um dia de um quarto selecionado
     * @param {Date} day - O dia em que ocorreu o clique.
     * @param {Object} attributes - Os atributos associados ao elemento clicado.
     */
    async mouseClick(day, attributes) {
      if (attributes.customData.type === 'resume') {
        return 0
      } else {
        this.$emit('showDayEdition', day, attributes.customData)
      }
    },
    /**
     * Inicializa o evento de mousemove e
     * seta os parâmetros iniciais e inicializa
     * a atualização dos frames para a animação
     * @param {Event} $event - O evento de movimento do mouse.
     * @param {Object} attributes - Os atributos associados ao elemento.
     */
    async bindMouseMove($event, attributes) {
      this.dayCurrentTarget = $event.currentTarget
      this.dayCurrentTargetContent = attributes

      this.dayCurrentTooltipTarget = document.querySelector(
        '.custom-details-tooltip'
      )

      this.dayCurrentTarget.addEventListener(
        'mousemove',
        this.updateMouseCoords
      )

      await this.$nextTick()
      this.seekMousePointer()
    },
    /**
     * Destroy o evento de mouse e cancela a
     * animação corrente
     */
    unbindMouseMove() {
      this.dayCurrentTarget.removeEventListener(
        'mousemove',
        this.updateMouseCoords
      )

      if (this.currentAnimationFrame)
        cancelAnimationFrame(this.currentAnimationFrame)
    },
    /** Atualiza as coordenadas da posição do mouse */
    updateMouseCoords(e) {
      this.mouseCoords.x = e.clientX
      this.mouseCoords.y = e.clientY
    },
    /**
     * Calcula a posição do da tooltip de acordo
     * com a posição do ponteiro do mouse
     */
    seekMousePointer() {
      /** Parâmetros dos limites do elemento */
      const tooltipRectParams = this.dayCurrentTooltipTarget.getBoundingClientRect()

      /** Velocidade que a tooltip se desloca */
      const speed = 0.08

      /** Espaço entre o cursor e a tooltip */
      const gutter = 10

      /** Distância X da tooltip para o cursor */
      let distX = this.mouseCoords.x - tooltipRectParams.left + gutter

      /** Distância Y da tooltip para o cursor */
      let distY = this.mouseCoords.y - tooltipRectParams.top + gutter

      /** Verifica se a posição futura vai cortar da viewport */
      const cut = this.willCutOfViewport(this.dayCurrentTooltipTarget, {
        distX,
        distY
      })

      /** Se cortou na direita, inverte */
      if (cut.right) {
        distX =
          this.mouseCoords.x -
          (tooltipRectParams.left + tooltipRectParams.width) -
          gutter
      }

      /** Se cortou embaixo, inverte */
      if (cut.bottom) {
        distY =
          this.mouseCoords.y -
          (tooltipRectParams.top + tooltipRectParams.height) -
          gutter
      }

      /** Se a tooltip está a uma distância superior a 200px */
      if (Math.abs(distX) > 200 || Math.abs(distY) > 200) {
        /** Move direto, sem animar */
        this.currentDescTooltipPosition.x += distX
        this.currentDescTooltipPosition.y += distY
      } else {
        /** Anima atrás do ponteiro do mouse */
        this.currentDescTooltipPosition.x += distX * speed
        this.currentDescTooltipPosition.y += distY * speed
      }

      this.currentAnimationFrame = requestAnimationFrame(this.seekMousePointer)
    },
    /**
     * Verifica se o elemento vai ser cortado da viewport
     *
     * @param {HTMLElement} elm - Elemento html
     * @param {Object} dist - Distância da tooltip para a nova posição do cursor
     *
     * @example dist {distX: Number, distY: Number}
     */
    willCutOfViewport(elm, dist) {
      const rect = elm.getBoundingClientRect()

      return {
        right: dist.distX + rect.left + rect.width > document.body.clientWidth,
        bottom: dist.distY + rect.top + rect.height > window.innerHeight
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.points-calendar {
  /** CALENDÁRIO */

  .disponibility {
    border-radius: 10px;
    box-sizing: content-box;
    font-size: font-size('size-x-small');
    font-weight: font-weight('weight-medium');

    &__default {
      color: pmw-colors(blue-100);
      padding: {
        left: 2px;
        right: 2px;
      }
    }

    &__no-disponibility {
      opacity: 0.5;
    }
  }

  .custom-calendar {
    /** CALENDARIO NORMAL */
    height: 20px;

    &__normal {
      .date-header {
        display: grid;
        grid-template-columns: 30px 1fr;
        align-items: flex-end;

        .price {
          text-align: end;
          &__normal {
            display: grid;
            justify-content: flex-end;
            padding-right: 10px;

            .compre-guest {
              color: pmw-colors(grey-40);
              text-align: end;
              margin-bottom: 8px;
            }

            .detail {
              margin-right: 4px;
            }
          }
        }
      }
    } /** FIM DO CALENDARIO NORMAL */

    &--day__disabled {
      .date-header {
        .day-label {
          margin-top: 4px;
        }
      }
    }
  }

  ::v-deep {
    .custom-calendar--day .opened .disponibility-flag,
    .custom-calendar--day .closed .disponibility-flag {
      background-color: pmw-colors(blue-100);
    }

    .custom-calendar {
      &.vc-container {
        .vc-day,
        .vc-weekday {
          width: calc(988px / 7); /** Tamanho dos dias da semana */
        }

        .vc-day {
          height: 100px;
          z-index: initial;

          &:hover {
            z-index: 2;
          }
        }
      }

      &--day-content-wrapper {
        grid-template-rows: 20px 1fr 20px;
        align-items: center;
        background-color: white;

        &.closed {
          background-color: pmw-colors(grey-10);
        }
        &.opened {
          background-color: $white;
        }
      }

      &--day {
        cursor: pointer;
        @include box-shadow(0px 0 0 transparent);
        transition: box-shadow $anim-fast linear;
        will-change: box-shadow;

        .date-content {
          margin-top: 50px;
          p + p {
            margin-top: 6px;
          }

          &-skeleton {
            &--circle {
              width: 10px;
              height: 10px;
              border-radius: 50%;
            }
            &--text {
              top: -32px;
            }
          }

          .disponibility {
            width: auto;
            margin: {
              top: 10px;
              bottom: 0px;
            }

            &__loader {
              width: 80%;
            }
          }
        }

        &:hover {
          @include box-shadow(0px 2px 15px rgba(0, 0, 0, 0.15));
        }

        &__disabled {
          cursor: default;

          &:hover {
            box-shadow: none;
          }
        }
      }

      /** CALENDÁRIO NORMAL */
      &__normal {
        &.vc-container {
          .vc-day {
            height: 110px;

            .guest {
              display: none;
            }

            .disponibility {
              margin: 0;
            }
          }
        }
      }
      /** FIM CALENDÁRIO NORMAL */

      /** CALENDÁRIO COMPRIMIDO */
    }
  }
}
</style>
